export interface Preceptor {
  id: string;
  name: string;
  rating: number;
  specialty: string;
  reviewCount: number;
  difficulty: number;
  wouldTakeAgain: number; // percentage
}

export type ReviewType = "rotation" | "internship" | "residency" | "fellowship";

export interface Review {
  id: string;
  type: ReviewType;

  title: string;
  organization: string;
  location: string;
  rating: number;
  difficulty: number;
  wouldDoAgain: boolean;
  review: string;
  author: string;
  date: string;
  tags: string[];
  thumbsUp: number;
  thumbsDown: number;
  userId?: string | null;
  isOwner?: boolean;
  major?: string;
  startDate?: string | null;
  endDate?: string | null;
  preceptors?: Preceptor[];
  compensation?: string | null;
  interviewInfo?: string | null;
  responsibilities?: string | null;
  tips?: string | null;
  recommendedFor?: string | null;
  experienceKey?: string | null;
  isPioneer?: boolean;
}


export const ROTATION_TAGS = [
  "Great Teaching",
  "Hands-On",
  "Long Hours",
  "Supportive Staff",
  "Diverse Cases",
  "Good Hours",
  "Heavy Workload",
  "Amazing Experience",
  "Tough but Worth It",
  "Lots of Autonomy",
  "Well Organized",
  "Poor Communication",
  "Flexible Schedule",
  "Research Opportunities",
  "Rural Setting",
];

export const INTERNSHIP_TAGS = [
  "Great Mentorship",
  "Real Projects",
  "Resume Builder",
  "Good Pay",
  "Free Food",
  "Creative",
  "Remote Friendly",
  "Leadership Opportunities",
  "Networking",
  "Steep Learning Curve",
  "Work-Life Balance",
  "Fast-Paced",
  "Collaborative Team",
  "Career Growth",
  "Diverse Environment",
];

export const ROTATION_CATEGORIES = [
  // Medical
  "Internal Medicine Rotation", "Family Medicine Rotation", "Pediatrics Rotation",
  "Surgery Rotation", "General Surgery Rotation", "Orthopedic Surgery Rotation",
  "Neurosurgery Rotation", "Cardiothoracic Surgery Rotation", "Plastic Surgery Rotation",
  "OB/GYN Rotation", "Obstetrics Rotation", "Gynecology Rotation",
  "Psychiatry Rotation", "Neurology Rotation", "Cardiology Rotation",
  "Dermatology Rotation", "Emergency Medicine Rotation", "Anesthesiology Rotation",
  "Radiology Rotation", "Pathology Rotation", "Oncology Rotation",
  "Endocrinology Rotation", "Gastroenterology Rotation", "Nephrology Rotation",
  "Pulmonology Rotation", "Rheumatology Rotation", "Infectious Disease Rotation",
  "Hematology Rotation", "Urology Rotation", "Ophthalmology Rotation",
  "Otolaryngology (ENT) Rotation", "Geriatrics Rotation", "Palliative Care Rotation",
  "Sports Medicine Rotation", "Physical Medicine & Rehab Rotation",
  // Pharmacy
  "Pharmacy Rotation", "Community Pharmacy Rotation", "Hospital Pharmacy Rotation",
  "Ambulatory Care Pharmacy Rotation", "Clinical Pharmacy Rotation",
  "Inpatient Pharmacy Rotation", "Oncology Pharmacy Rotation",
  "Pediatric Pharmacy Rotation", "Critical Care Pharmacy Rotation",
  "Infectious Disease Pharmacy Rotation", "Managed Care Pharmacy Rotation",
  "Pharmaceutical Industry Rotation",
  // Nursing
  "Nursing Rotation", "Med-Surg Nursing Rotation", "ICU Nursing Rotation",
  "ER Nursing Rotation", "Labor & Delivery Nursing Rotation",
  "Pediatric Nursing Rotation", "Psychiatric Nursing Rotation",
  "Community Health Nursing Rotation", "Oncology Nursing Rotation",
  "Operating Room Nursing Rotation", "Public Health Nursing Rotation",
  "Home Health Nursing Rotation",
  // Dental
  "Dental Rotation", "General Dentistry Rotation", "Pediatric Dentistry Rotation",
  "Oral Surgery Rotation", "Endodontics Rotation", "Periodontics Rotation",
  "Orthodontics Rotation", "Prosthodontics Rotation",
  // Veterinary
  "Veterinary Rotation", "Small Animal Veterinary Rotation",
  "Large Animal Veterinary Rotation", "Equine Veterinary Rotation",
  "Exotic Animal Veterinary Rotation", "Veterinary Surgery Rotation",
  "Veterinary Emergency Rotation",
  // PA / NP
  "Physician Assistant Rotation", "Nurse Practitioner Rotation",
  // Allied Health
  "Physical Therapy Rotation", "Occupational Therapy Rotation",
  "Speech-Language Pathology Rotation", "Respiratory Therapy Rotation",
  "Athletic Training Rotation", "Audiology Rotation", "Optometry Rotation",
  "Podiatry Rotation", "Chiropractic Rotation",
  // Mental health / Social
  "Clinical Psychology Rotation", "Counseling Rotation", "Social Work Rotation",
  // Lab / Diagnostic
  "Medical Laboratory Rotation", "Radiologic Technology Rotation",
  "Sonography Rotation", "Nuclear Medicine Rotation",
  // Public health / Nutrition
  "Public Health Rotation", "Nutrition / Dietetics Rotation",
  "Epidemiology Rotation",
];

export const INTERNSHIP_CATEGORIES = [
  // Business
  "Management Internship", "Business Operations Internship",
  "Strategy Internship", "Consulting Internship", "Project Management Internship",
  // Finance
  "Finance Internship", "Accounting Internship", "Investment Banking Internship",
  "Private Equity Internship", "Venture Capital Internship",
  "Wealth Management Internship", "Equity Research Internship",
  "Financial Analyst Internship", "Actuarial Internship",
  // Marketing / Comms
  "Marketing Internship", "Digital Marketing Internship",
  "Brand Marketing Internship", "Content Marketing Internship",
  "Social Media Internship", "SEO Internship",
  "Communications Internship", "Public Relations Internship",
  "Advertising Internship", "Copywriting Internship",
  // Tech
  "Software Engineering Internship", "Frontend Engineering Internship",
  "Backend Engineering Internship", "Full-Stack Engineering Internship",
  "Mobile Engineering Internship", "DevOps Internship",
  "Site Reliability Engineering Internship", "Security / Cybersecurity Internship",
  "QA / Testing Internship", "IT Internship",
  // Data / AI
  "Data Science Internship", "Data Analytics Internship",
  "Data Engineering Internship", "Machine Learning Internship",
  "AI Research Internship", "Business Intelligence Internship",
  // Design / Product
  "Product Management Internship", "Product Design Internship",
  "UX Design Internship", "UI Design Internship",
  "Graphic Design Internship", "Industrial Design Internship",
  "UX Research Internship",
  // Engineering (non-software)
  "Mechanical Engineering Internship", "Electrical Engineering Internship",
  "Civil Engineering Internship", "Chemical Engineering Internship",
  "Biomedical Engineering Internship", "Aerospace Engineering Internship",
  "Industrial Engineering Internship", "Environmental Engineering Internship",
  "Manufacturing Engineering Internship",
  // Sales / Ops
  "Sales Internship", "Business Development Internship",
  "Operations Internship", "Supply Chain Internship",
  "Logistics Internship", "Procurement Internship",
  // People
  "Human Resources Internship", "People Operations Internship",
  "Recruiting Internship", "Talent Acquisition Internship",
  // Legal / Policy
  "Legal Internship", "Paralegal Internship",
  "Government Internship", "Policy Internship", "Lobbying Internship",
  // Media / Creative
  "Journalism Internship", "Editorial Internship",
  "Film / Video Production Internship", "Photography Internship",
  "Music Industry Internship", "Publishing Internship",
  // Nonprofit / Education / Health admin
  "Nonprofit Internship", "Education Internship", "Teaching Internship",
  "Healthcare Administration Internship", "Hospital Administration Internship",
  "Public Health Internship", "Research Internship",
  "Lab Research Internship", "Clinical Research Internship",
  // Architecture / Real estate
  "Architecture Internship", "Real Estate Internship",
  "Urban Planning Internship", "Construction Management Internship",
];

export const RESIDENCY_TAGS = [
  "Great Teaching",
  "Strong Didactics",
  "Supportive Program Director",
  "Heavy Call Schedule",
  "Good Work-Life Balance",
  "High Volume",
  "Research Opportunities",
  "Malignant Culture",
  "Collegial Co-Residents",
  "Strong Board Prep",
  "Lots of Autonomy",
  "Well Organized",
  "Poor Communication",
  "Great Fellowship Placement",
  "Underserved Population",
];

export const FELLOWSHIP_TAGS = [
  "Strong Mentorship",
  "Research Heavy",
  "High Clinical Volume",
  "Great Procedural Training",
  "Good Work-Life Balance",
  "Heavy Call Schedule",
  "Supportive Faculty",
  "Excellent Job Placement",
  "Publication Opportunities",
  "Lots of Autonomy",
  "Well Organized",
  "Niche Subspecialty Exposure",
  "National Conference Support",
  "Collegial Co-Fellows",
  "Underserved Population",
];

export const RESIDENCY_CATEGORIES = [
  // Physician residencies
  "Internal Medicine Residency", "Family Medicine Residency", "Pediatrics Residency",
  "General Surgery Residency", "Orthopedic Surgery Residency", "Neurosurgery Residency",
  "Plastic Surgery Residency", "Vascular Surgery Residency", "Cardiothoracic Surgery Residency",
  "OB/GYN Residency", "Psychiatry Residency", "Neurology Residency",
  "Emergency Medicine Residency", "Anesthesiology Residency", "Radiology Residency",
  "Interventional Radiology Residency", "Radiation Oncology Residency",
  "Pathology Residency", "Dermatology Residency", "Ophthalmology Residency",
  "Otolaryngology (ENT) Residency", "Urology Residency",
  "Physical Medicine & Rehab Residency", "Preventive Medicine Residency",
  "Med-Peds Residency", "Transitional Year Residency",
  // Pharmacy
  "PGY1 Pharmacy Residency", "PGY2 Pharmacy Residency",
  "PGY1 Community Pharmacy Residency", "PGY2 Ambulatory Care Residency",
  "PGY2 Critical Care Pharmacy Residency", "PGY2 Oncology Pharmacy Residency",
  "PGY2 Infectious Diseases Pharmacy Residency", "PGY2 Pediatric Pharmacy Residency",
  "PGY2 Emergency Medicine Pharmacy Residency", "PGY2 Psychiatric Pharmacy Residency",
  "Health-System Pharmacy Administration Residency",
  // Dental / other
  "General Practice Dental Residency", "Advanced Education in General Dentistry",
  "Oral & Maxillofacial Surgery Residency", "Pediatric Dentistry Residency",
  "Orthodontics Residency", "Periodontics Residency", "Endodontics Residency",
  "Veterinary Residency", "Optometry Residency", "Podiatry Residency",
  "Nurse Anesthesia Residency", "Nurse Practitioner Residency",
  "Physician Assistant Residency", "Clinical Psychology Residency",
  "Dietetic Internship / Residency", "Physical Therapy Residency",
  "Occupational Therapy Residency", "Speech-Language Pathology Residency",
];

export const FELLOWSHIP_CATEGORIES = [
  // Medicine subspecialties
  "Cardiology Fellowship", "Interventional Cardiology Fellowship",
  "Electrophysiology Fellowship", "Gastroenterology Fellowship",
  "Pulmonary / Critical Care Fellowship", "Critical Care Medicine Fellowship",
  "Nephrology Fellowship", "Endocrinology Fellowship", "Rheumatology Fellowship",
  "Infectious Disease Fellowship", "Hematology / Oncology Fellowship",
  "Geriatrics Fellowship", "Palliative Care Fellowship", "Allergy & Immunology Fellowship",
  "Sports Medicine Fellowship", "Sleep Medicine Fellowship", "Addiction Medicine Fellowship",
  // Surgery / procedural
  "Surgical Oncology Fellowship", "Trauma / Acute Care Surgery Fellowship",
  "Colorectal Surgery Fellowship", "Minimally Invasive Surgery Fellowship",
  "Transplant Surgery Fellowship", "Hand Surgery Fellowship",
  "Spine Surgery Fellowship", "Pediatric Surgery Fellowship",
  "Vascular Surgery Fellowship", "Breast Surgery Fellowship",
  // Peds / OB / neuro / psych
  "Neonatology Fellowship", "Pediatric Cardiology Fellowship",
  "Pediatric Emergency Medicine Fellowship", "Pediatric Hematology / Oncology Fellowship",
  "Maternal-Fetal Medicine Fellowship", "Reproductive Endocrinology Fellowship",
  "Gynecologic Oncology Fellowship", "Urogynecology Fellowship",
  "Stroke / Vascular Neurology Fellowship", "Neurocritical Care Fellowship",
  "Epilepsy Fellowship", "Movement Disorders Fellowship",
  "Child & Adolescent Psychiatry Fellowship", "Forensic Psychiatry Fellowship",
  "Consultation-Liaison Psychiatry Fellowship",
  // Imaging / path / anesthesia
  "Body Imaging Fellowship", "Neuroradiology Fellowship", "Musculoskeletal Radiology Fellowship",
  "Breast Imaging Fellowship", "Cytopathology Fellowship", "Hematopathology Fellowship",
  "Forensic Pathology Fellowship", "Pain Medicine Fellowship",
  "Cardiac Anesthesiology Fellowship", "Pediatric Anesthesiology Fellowship",
  // Non-physician / research / policy
  "Pharmacy Fellowship", "Pharmaceutical Industry Fellowship",
  "Medical Affairs Fellowship", "Regulatory Affairs Fellowship",
  "Clinical Research Fellowship", "Global Health Fellowship",
  "Health Policy Fellowship", "Public Health Fellowship",
  "Government Fellowship", "Nonprofit Leadership Fellowship",
  "Teaching Fellowship", "Postdoctoral Research Fellowship",
];

export interface ReviewTypeConfig {
  label: string;
  labelPlural: string;
  path: string;
  categoryLabel: string;
  categoryPlaceholder: string;
  orgLabel: string;
  orgPlaceholder: string;
  categories: string[];
  tags: string[];
  showCompensation: boolean;
  hasPreceptors: boolean;
  requiresTitle: boolean;
}

export const REVIEW_TYPES: ReviewType[] = ["rotation", "residency", "fellowship", "internship"];

export const REVIEW_TYPE_CONFIG: Record<ReviewType, ReviewTypeConfig> = {
  rotation: {
    label: "Rotation",
    labelPlural: "Rotations",
    path: "/rotations",
    categoryLabel: "Rotation Type",
    categoryPlaceholder: "Start typing… e.g. Dental, Veterinary, Nursing",
    orgLabel: "Hospital / Clinic",
    orgPlaceholder: "e.g. City General Hospital",
    categories: ROTATION_CATEGORIES,
    tags: ROTATION_TAGS,
    showCompensation: false,
    hasPreceptors: true,
    requiresTitle: false,
  },
  residency: {
    label: "Residency",
    labelPlural: "Residencies",
    path: "/residencies",
    categoryLabel: "Residency Program Type",
    categoryPlaceholder: "Start typing… e.g. Internal Medicine, PGY1 Pharmacy",
    orgLabel: "Hospital / Program Site",
    orgPlaceholder: "e.g. Mount Sinai Hospital",
    categories: RESIDENCY_CATEGORIES,
    tags: RESIDENCY_TAGS,
    showCompensation: true,
    hasPreceptors: true,
    requiresTitle: false,
  },
  fellowship: {
    label: "Fellowship",
    labelPlural: "Fellowships",
    path: "/fellowships",
    categoryLabel: "Fellowship Type",
    categoryPlaceholder: "Start typing… e.g. Cardiology, Medical Affairs",
    orgLabel: "Hospital / Institution",
    orgPlaceholder: "e.g. Cleveland Clinic",
    categories: FELLOWSHIP_CATEGORIES,
    tags: FELLOWSHIP_TAGS,
    showCompensation: true,
    hasPreceptors: true,
    requiresTitle: false,
  },
  internship: {
    label: "Internship",
    labelPlural: "Internships",
    path: "/internships",
    categoryLabel: "Internship Field",
    categoryPlaceholder: "Start typing… e.g. Marketing, Software, Finance",
    orgLabel: "Company",
    orgPlaceholder: "e.g. TechCorp Solutions",
    categories: INTERNSHIP_CATEGORIES,
    tags: INTERNSHIP_TAGS,
    showCompensation: true,
    hasPreceptors: false,
    requiresTitle: true,
  },
};

export const getReviewTypeConfig = (type?: string | null): ReviewTypeConfig =>
  REVIEW_TYPE_CONFIG[(type as ReviewType) in REVIEW_TYPE_CONFIG ? (type as ReviewType) : "rotation"];


export const MAJORS = [
  // Health
  "Nursing", "Pre-Med", "Biology", "Biochemistry", "Biomedical Sciences",
  "Public Health", "Health Sciences", "Healthcare Administration",
  "Kinesiology", "Exercise Science", "Athletic Training",
  "Nutrition / Dietetics", "Pharmacy", "Pre-Pharmacy",
  "Pre-Dental", "Dental Hygiene", "Pre-Veterinary", "Veterinary Sciences",
  "Pre-PA (Physician Assistant)", "Pre-PT (Physical Therapy)",
  "Occupational Therapy", "Speech-Language Pathology", "Audiology",
  "Radiologic Sciences", "Respiratory Therapy", "Medical Laboratory Sciences",
  "Social Work", "Psychology", "Neuroscience", "Counseling",
  // STEM
  "Chemistry", "Physics", "Mathematics", "Statistics", "Data Science",
  "Computer Science", "Computer Engineering", "Software Engineering",
  "Information Systems", "Information Technology", "Cybersecurity",
  "Mechanical Engineering", "Electrical Engineering", "Civil Engineering",
  "Chemical Engineering", "Biomedical Engineering", "Aerospace Engineering",
  "Industrial Engineering", "Environmental Engineering", "Materials Science",
  "Environmental Science", "Geology", "Astronomy",
  // Business
  "Business Administration", "Finance", "Accounting", "Economics",
  "Marketing", "Management", "Entrepreneurship", "Supply Chain Management",
  "International Business", "Human Resources", "Real Estate",
  // Humanities / Social Sciences
  "English", "History", "Philosophy", "Political Science", "Sociology",
  "Anthropology", "Linguistics", "Religious Studies", "Gender Studies",
  "International Relations", "Criminal Justice", "Pre-Law",
  "Communications", "Journalism", "Public Relations",
  // Arts / Design
  "Graphic Design", "Industrial Design", "UX/UI Design", "Fine Arts",
  "Architecture", "Interior Design", "Film / Media Production",
  "Music", "Theater", "Photography", "Creative Writing",
  // Education
  "Elementary Education", "Secondary Education", "Special Education",
  // Other
  "Agriculture", "Hospitality Management", "Sports Management",
  "Urban Planning", "Liberal Arts", "Undecided", "Other",
];

export const mockReviews: Review[] = [
  {
    id: "1",
    type: "rotation",
    title: "Internal Medicine",
    organization: "City General Hospital",
    location: "New York, NY",
    rating: 4.5,
    difficulty: 3.5,
    wouldDoAgain: true,
    review: "Excellent teaching attendings. Great exposure to a wide variety of cases. The residents were very supportive and willing to teach.",
    author: "Anonymous Student",
    date: "2025-12-15",
    tags: ["Great Teaching", "Diverse Cases"],
    thumbsUp: 24,
    thumbsDown: 2,
    preceptors: [
      { id: "p1", name: "Dr. Sarah Chen", rating: 4.5, specialty: "Cardiology", reviewCount: 12, difficulty: 3, wouldTakeAgain: 92 },
      { id: "p2", name: "Dr. James Morton", rating: 5, specialty: "General Medicine", reviewCount: 8, difficulty: 2.5, wouldTakeAgain: 100 },
    ],
  },
  {
    id: "2",
    type: "rotation",
    title: "Pediatrics",
    organization: "Children's Medical Center",
    location: "Boston, MA",
    rating: 5,
    difficulty: 2.5,
    wouldDoAgain: true,
    review: "Best rotation of my third year. The staff genuinely cares about student education. You get to do a lot of hands-on work.",
    author: "Anonymous Student",
    date: "2025-11-20",
    tags: ["Hands-On", "Supportive Staff"],
    thumbsUp: 31,
    thumbsDown: 1,
    preceptors: [
      { id: "p3", name: "Dr. Maria Lopez", rating: 5, specialty: "Pediatric Oncology", reviewCount: 15, difficulty: 2, wouldTakeAgain: 97 },
      { id: "p4", name: "Dr. Kevin Park", rating: 4, specialty: "Neonatology", reviewCount: 6, difficulty: 3, wouldTakeAgain: 83 },
    ],
  },
  {
    id: "3",
    type: "rotation",
    title: "Surgery",
    organization: "University Hospital",
    location: "Chicago, IL",
    rating: 3,
    difficulty: 4.5,
    wouldDoAgain: false,
    review: "Long hours but good surgical exposure. Teaching varies by attending. Make sure to be proactive.",
    author: "Anonymous Student",
    date: "2025-10-08",
    tags: ["Long Hours", "Heavy Workload"],
    thumbsUp: 15,
    thumbsDown: 5,
    preceptors: [
      { id: "p5", name: "Dr. Robert Kim", rating: 3.5, specialty: "Trauma Surgery", reviewCount: 10, difficulty: 4.5, wouldTakeAgain: 55 },
      { id: "p6", name: "Dr. Angela White", rating: 4.5, specialty: "Orthopedics", reviewCount: 9, difficulty: 3, wouldTakeAgain: 89 },
    ],
  },
  {
    id: "4",
    type: "rotation",
    title: "Psychiatry",
    organization: "Metro Behavioral Health",
    location: "San Francisco, CA",
    rating: 4,
    difficulty: 2,
    wouldDoAgain: true,
    review: "Very interesting patient population. Great for anyone considering psychiatry. Reasonable hours.",
    author: "Anonymous Student",
    date: "2025-09-25",
    tags: ["Good Hours", "Amazing Experience"],
    thumbsUp: 18,
    thumbsDown: 3,
    preceptors: [
      { id: "p7", name: "Dr. Lisa Nguyen", rating: 4, specialty: "Child Psychiatry", reviewCount: 7, difficulty: 2, wouldTakeAgain: 86 },
    ],
  },
  {
    id: "5",
    type: "internship",
    title: "Software Engineering Intern",
    organization: "TechCorp Solutions",
    location: "Austin, TX",
    rating: 4.5,
    difficulty: 3,
    wouldDoAgain: true,
    review: "Fantastic mentorship program. Got to work on real production features. Free lunch and great culture.",
    author: "CS Major",
    date: "2025-08-30",
    tags: ["Great Mentorship", "Real Projects", "Free Food"],
    thumbsUp: 42,
    thumbsDown: 3,
  },
  {
    id: "6",
    type: "internship",
    title: "Data Science Intern",
    organization: "Analytics Plus",
    location: "Seattle, WA",
    rating: 3.5,
    difficulty: 4,
    wouldDoAgain: false,
    review: "Interesting data problems but felt siloed from the rest of the team. Good for resume building.",
    author: "Data Science Student",
    date: "2025-11-12",
    tags: ["Resume Builder", "Steep Learning Curve"],
    thumbsUp: 11,
    thumbsDown: 4,
  },
  {
    id: "7",
    type: "internship",
    title: "Marketing Intern",
    organization: "BrandWave Agency",
    location: "Los Angeles, CA",
    rating: 5,
    difficulty: 2,
    wouldDoAgain: true,
    review: "Incredible creative environment. Got to lead my own campaign by the end. Would highly recommend.",
    author: "Marketing Student",
    date: "2025-12-01",
    tags: ["Creative", "Leadership Opportunities"],
    thumbsUp: 28,
    thumbsDown: 0,
  },
  {
    id: "8",
    type: "internship",
    title: "Finance Intern",
    organization: "Global Investments Ltd",
    location: "New York, NY",
    rating: 4,
    difficulty: 4.5,
    wouldDoAgain: true,
    review: "Great exposure to financial modeling and client presentations. Steep learning curve but very rewarding.",
    author: "Finance Major",
    date: "2025-10-15",
    tags: ["Steep Learning Curve", "Networking"],
    thumbsUp: 19,
    thumbsDown: 2,
  },
];
